import { z } from 'zod';
import { ACQUISITION_DEFAULTS, ACQUISITION_MAX_UNITS, acquisitionCostsPctOf, calculateAcquisition, } from '../formulas/acquisition.js';
/**
 * Acquisition calculation of the wizard (Objekt anlegen Schritt 4 „Kalkulation", `GET`/`POST
 * /properties/:id/calculations`, docs/dev/api-properties.md § Step 4) in Nico Kremer's model (owner decision Paul
 * 30.10.2026, D-351…D-357): his inputs, his unit list („IST-Mietaufstellung", stored per version as a snapshot) and
 * his result blocks. Versions saved before migration 0052 carry the old seven inputs; they are read through the
 * legacy mapping (`legacy: true`, „aus alter Kalkulation übernommen"). Shared by the API (validation, serializer)
 * and the SPA (types).
 */
/** Nico's inputs in the order of his calculator. Money in cents, percentages plain (3.57 = 3,57 %). */
export const ACQUISITION_FIELDS = [
    'purchasePriceCents',
    'brokerCommissionPct',
    'landTransferTaxPct',
    'notaryPct',
    'capexCents',
    'financingCostsCents',
    'divisionCostsCents',
    'targetSalePricePerSqmCents',
    'salesCommissionPct',
    'interestPct',
    'repaymentPct',
    'depreciationPct',
    'buildingSharePct',
    'taxRatePct',
    'buyerAcquisitionCostsPct',
    'unitManagementCents',
    'hoaManagementCents',
    'maintenancePerSqmCents',
    'otherCostsPerSqmCents',
    'rentSubsidyPct',
    'rentSubsidyMonths',
];
/** A field source key: one of Nico's inputs or the unit list as a whole. */
export const ACQUISITION_SOURCE_KEYS = [...ACQUISITION_FIELDS, 'units'];
/**
 * Figures the document classification reads (`ExtractedFigures` of the API): Kaufpreis and Käufer-Maklercourtage
 * (from the exposé, D-373) are Nico's inputs, Wohnfläche gesamt and Ist-Miete p. a. fill the unit list while it is
 * empty (one summary row), Erhaltungsrücklage is shown only.
 */
export const ACQUISITION_DOCUMENT_FIGURES = [
    'purchasePriceCents',
    'brokerCommissionPct',
    'livingAreaTotalSqm',
    'actualRentPaCents',
    'maintenanceReserveCents',
];
/** Document figures that are Nico's inputs themselves (their source is the input's source). */
export const ACQUISITION_INPUT_DOCUMENT_FIGURES = [
    'purchasePriceCents',
    'brokerCommissionPct',
];
/** Field source of a value that nobody took from a document („manuell"). */
export const ACQUISITION_MANUAL_SOURCE = 'manuell';
/** Note of a version saved before migration 0052, read through the legacy mapping. */
export const ACQUISITION_LEGACY_NOTE = 'aus alter Kalkulation übernommen';
/** Source of a value that is one of Nico's defaults (`ACQUISITION_DEFAULTS`). */
export const ACQUISITION_DEFAULT_SOURCE = 'Standardwert';
/** Source of a unit list taken from the property's units. */
export const ACQUISITION_UNITS_SOURCE = 'aus Einheiten des Objekts';
/**
 * „Einheit" of the one summary row that stands for the whole building: the legacy mapping (Wohnfläche gesamt +
 * Ist-Miete p. a.) and figures read from a document while the unit list is still empty.
 */
export const ACQUISITION_SUMMARY_UNIT_NUMBER = 'Gesamtobjekt';
const MAX_CENTS = 9_000_000_000_000;
const cents = z.number().int().min(0).max(MAX_CENTS);
const pct = z.number().min(0).max(100);
/**
 * Nico's options of 01.11.2026 (owner decision 02.11.2026, migration 0054, D-369…D-372): „Rechenweg"
 * (Kaufpreis → Marge | Zielmarge → Kaufpreis), „Kaufnebenkosten zahlt" (Käufer | Verkäufer) and „Gewerbeanteil nach"
 * (Fläche | Miete; applies only when the unit list has a Gewerbe row). A version saved before 0054 reads as
 * forward / buyer / area.
 */
export const ACQUISITION_MODES = ['forward', 'target_margin'];
export const ACQUISITION_COSTS_PAYERS = ['buyer', 'seller'];
export const ACQUISITION_COMMERCIAL_SPLITS = ['area', 'rent'];
/** The options a calculation without them reads as (pre-0054 versions, a new calculation). */
export const ACQUISITION_OPTION_DEFAULTS = {
    mode: 'forward',
    targetMarginPct: null,
    acquisitionCostsPayer: 'buyer',
    commercialSplit: 'area',
};
/**
 * A source the document classification wrote („aus <Datei>"), not one of the fixed „aus …" sources. The
 * Käufer-Maklercourtage is a document figure only with such a source (its default is „Standardwert").
 */
export function isDocumentSource(source) {
    return (source.startsWith('aus ') &&
        source !== ACQUISITION_LEGACY_NOTE &&
        source !== ACQUISITION_UNITS_SOURCE &&
        source !== ACQUISITION_TARGET_MARGIN_SOURCE);
}
/** Highest „Zielmarge % auf GIK" (the column is numeric(5,2)). */
export const ACQUISITION_MAX_TARGET_MARGIN_PCT = 999.99;
/** Source of the purchase price in the target_margin mode („max. Kaufpreis"). */
export const ACQUISITION_TARGET_MARGIN_SOURCE = 'aus Zielmarge';
export const acquisitionOptionsSchema = z.object({
    mode: z.enum(ACQUISITION_MODES),
    targetMarginPct: z.number().min(0).max(ACQUISITION_MAX_TARGET_MARGIN_PCT).nullable(),
    acquisitionCostsPayer: z.enum(ACQUISITION_COSTS_PAYERS),
    commercialSplit: z.enum(ACQUISITION_COMMERCIAL_SPLITS),
});
export const acquisitionInputsSchema = z.object({
    purchasePriceCents: cents.nullable(),
    brokerCommissionPct: pct.nullable(),
    landTransferTaxPct: pct.nullable(),
    notaryPct: pct.nullable(),
    capexCents: cents.nullable(),
    financingCostsCents: cents.nullable(),
    divisionCostsCents: cents.nullable(),
    targetSalePricePerSqmCents: cents.nullable(),
    salesCommissionPct: pct.nullable(),
    interestPct: pct.nullable(),
    repaymentPct: pct.nullable(),
    depreciationPct: pct.nullable(),
    buildingSharePct: pct.nullable(),
    taxRatePct: pct.nullable(),
    buyerAcquisitionCostsPct: pct.nullable(),
    unitManagementCents: cents.nullable(),
    hoaManagementCents: cents.nullable(),
    maintenancePerSqmCents: cents.nullable(),
    otherCostsPerSqmCents: cents.nullable(),
    rentSubsidyPct: pct.nullable(),
    rentSubsidyMonths: z.number().int().min(0).max(600).nullable(),
});
/** One row of the unit list („Lage", „Einheit", „m²", „Kaltmiete/M", „Art" Wohnen/Gewerbe). */
export const acquisitionUnitRowSchema = z.object({
    position: z.string().trim().max(80),
    number: z.string().trim().max(40),
    /** m² with up to two decimals, as in Nico's sheet („88,26"). */
    areaSqm: z
        .number()
        .min(0)
        .max(100_000)
        .refine((value) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-6, {
        message: 'Bitte die Fläche mit höchstens zwei Nachkommastellen angeben.',
    }),
    rentCents: cents,
    residential: z.boolean(),
});
export const createAcquisitionCalculationBodySchema = acquisitionInputsSchema
    .extend({
    units: z.array(acquisitionUnitRowSchema).max(ACQUISITION_MAX_UNITS),
    note: z.string().trim().max(200).nullable().optional(),
    /** Source per field („aus Mietliste_Stand_09-2026.xlsx", „Landessatz Bayern"); fields without one count as „manuell". */
    fieldSources: z.partialRecord(z.enum(ACQUISITION_SOURCE_KEYS), z.string().trim().max(200)).optional(),
    /** Absent = forward / buyer / area (clients from before 0054). */
    mode: z.enum(ACQUISITION_MODES).optional(),
    targetMarginPct: z.number().min(0).max(ACQUISITION_MAX_TARGET_MARGIN_PCT).nullable().optional(),
    acquisitionCostsPayer: z.enum(ACQUISITION_COSTS_PAYERS).optional(),
    commercialSplit: z.enum(ACQUISITION_COMMERCIAL_SPLITS).optional(),
})
    .superRefine((body, ctx) => {
    if (body.mode === 'target_margin' &&
        (body.targetMarginPct === null || body.targetMarginPct === undefined))
        ctx.addIssue({
            code: 'custom',
            path: ['targetMarginPct'],
            message: 'Bitte die Zielmarge % auf GIK angeben.',
        });
    if (body.acquisitionCostsPayer === 'seller' &&
        acquisitionCostsPctOf({
            brokerCommissionPct: body.brokerCommissionPct ?? 0,
            landTransferTaxPct: body.landTransferTaxPct ?? 0,
            notaryPct: body.notaryPct ?? 0,
        }) >= 100)
        ctx.addIssue({
            code: 'custom',
            path: ['acquisitionCostsPayer'],
            message: 'Die Kaufnebenkosten müssen unter 100 % liegen, wenn der Verkäufer sie zahlt.',
        });
});
export const acquisitionUnitResultSchema = acquisitionUnitRowSchema.extend({
    salePriceCents: z.number().int(),
    installmentCents: z.number().int(),
    taxBenefitCents: z.number().int(),
    netWithoutSubsidyCents: z.number().int(),
    subsidyActive: z.boolean(),
    subsidyOneOffCents: z.number().int(),
    netWithSubsidyCents: z.number().int(),
});
/** `calculateAcquisition()` of `formulas/acquisition.ts`; null while Kaufpreis, Ziel-Verkaufspreis or units are missing. */
export const acquisitionResultsSchema = z.object({
    totalAreaSqm: z.number(),
    rentMonthlyCents: z.number().int(),
    rentAnnualCents: z.number().int(),
    brokerCommissionCents: z.number().int(),
    landTransferTaxCents: z.number().int(),
    notaryCents: z.number().int(),
    acquisitionCostCents: z.number().int(),
    totalInvestmentCents: z.number().int(),
    commercialShareCents: z.number().int(),
    commercialSharePct: z.number(),
    commercialSplitBasis: z.enum(ACQUISITION_COMMERCIAL_SPLITS).nullable(),
    gikResidentialCents: z.number().int(),
    effectivePurchasePriceCents: z.number().int(),
    maxPurchasePriceCents: z.number().int().nullable(),
    acquisitionCostsPct: z.number(),
    saleBaseCents: z.number().int(),
    rentSubsidyTotalCents: z.number().int(),
    saleInclSubsidyCents: z.number().int(),
    salesCommissionCents: z.number().int(),
    handoverPriceCents: z.number().int(),
    marginCents: z.number().int(),
    marginOnTotalInvestmentPct: z.number(),
    purchaseFactor: z.number(),
    saleFactor: z.number(),
    grossYieldPct: z.number(),
    salePricePerSqmCents: z.number().int(),
    units: z.array(acquisitionUnitResultSchema),
});
const personSchema = z.object({ id: z.string(), name: z.string(), shortName: z.string() });
export const acquisitionCalculationVersionSchema = z.object({
    id: z.string(),
    version: z.number().int(),
    /** „V3" */
    label: z.string(),
    current: z.boolean(),
    note: z.string().nullable(),
    author: personSchema.nullable(),
    /** „J. Weiler · Cap-Ex / Sanierung angepasst" | „Automatisch aus Unterlagen" */
    summary: z.string(),
    createdAt: z.string(),
    /** Saved before migration 0052: inputs and units come from the legacy mapping. */
    legacy: z.boolean(),
    /** `ACQUISITION_LEGACY_NOTE` for a legacy version, else null. */
    legacyNote: z.string().nullable(),
    inputs: acquisitionInputsSchema,
    /** „Rechenweg", „Kaufnebenkosten zahlt", „Gewerbeanteil nach" (pre-0054 versions: forward / buyer / area). */
    options: acquisitionOptionsSchema,
    /** The unit list the version was computed with (snapshot). */
    units: z.array(acquisitionUnitRowSchema),
    /**
     * Figures read from documents (Objekt anlegen 03 „Bisher erkannt"): Kaufpreis, Wohnfläche gesamt, Ist-Miete p. a.,
     * Erhaltungsrücklage with the source they came from; only values whose source is not „manuell".
     */
    documentFigures: z.array(z.object({
        key: z.enum(ACQUISITION_DOCUMENT_FIGURES),
        value: z.number(),
        source: z.string(),
    })),
    /** Per field with a value (and `units` when the list is not empty): { source, manual }. */
    fieldSources: z.partialRecord(z.enum(ACQUISITION_SOURCE_KEYS), z.object({ source: z.string(), manual: z.boolean() })),
    results: acquisitionResultsSchema.nullable(),
});
/** Values a new calculation starts from: the property's land transfer tax (D-199) and its units. */
export const acquisitionPrefillSchema = z.object({
    landTransferTaxPct: z.number(),
    /** „Landessatz Bayern" | „aus Objektdaten" | „Bundesland unbekannt" */
    landTransferTaxSource: z.string(),
    units: z.array(acquisitionUnitRowSchema),
    /** „aus Einheiten des Objekts" when the property has units, else null. */
    unitsSource: z.string().nullable(),
});
export const acquisitionCalculationsResponseSchema = z.object({
    items: z.array(acquisitionCalculationVersionSchema),
    current: acquisitionCalculationVersionSchema.nullable(),
    prefill: acquisitionPrefillSchema,
});
/**
 * The results of a version or draft: null while Kaufpreis (forward) or Zielmarge (target_margin), Ziel-
 * Verkaufspreis je m² or the unit list are missing, or while the seller pays NK of 100 % or more; otherwise
 * `calculateAcquisition()` with every other empty input as 0 (an empty field adds nothing — the wizard seeds Nico's
 * defaults, so 0 only stands where someone cleared a field or a legacy version has no such input).
 */
export function acquisitionResultsOf(inputs, units, options = ACQUISITION_OPTION_DEFAULTS) {
    const target = options.mode === 'target_margin';
    if (target ? options.targetMarginPct === null : inputs.purchasePriceCents === null)
        return null;
    if (inputs.targetSalePricePerSqmCents === null || units.length === 0)
        return null;
    const costs = acquisitionCostsPctOf({
        brokerCommissionPct: inputs.brokerCommissionPct ?? 0,
        landTransferTaxPct: inputs.landTransferTaxPct ?? 0,
        notaryPct: inputs.notaryPct ?? 0,
    });
    if (options.acquisitionCostsPayer === 'seller' && costs >= 100)
        return null;
    return calculateAcquisition({
        mode: options.mode,
        targetMarginPct: options.targetMarginPct ?? 0,
        acquisitionCostsPayer: options.acquisitionCostsPayer,
        commercialSplit: options.commercialSplit,
        purchasePriceCents: inputs.purchasePriceCents ?? 0,
        brokerCommissionPct: inputs.brokerCommissionPct ?? 0,
        landTransferTaxPct: inputs.landTransferTaxPct ?? 0,
        notaryPct: inputs.notaryPct ?? 0,
        capexCents: inputs.capexCents ?? 0,
        financingCostsCents: inputs.financingCostsCents ?? 0,
        divisionCostsCents: inputs.divisionCostsCents ?? 0,
        targetSalePricePerSqmCents: inputs.targetSalePricePerSqmCents,
        salesCommissionPct: inputs.salesCommissionPct ?? 0,
        interestPct: inputs.interestPct ?? 0,
        repaymentPct: inputs.repaymentPct ?? 0,
        depreciationPct: inputs.depreciationPct ?? 0,
        buildingSharePct: inputs.buildingSharePct ?? 0,
        taxRatePct: inputs.taxRatePct ?? 0,
        buyerAcquisitionCostsPct: inputs.buyerAcquisitionCostsPct ?? 0,
        unitManagementCents: inputs.unitManagementCents ?? 0,
        hoaManagementCents: inputs.hoaManagementCents ?? 0,
        maintenancePerSqmCents: inputs.maintenancePerSqmCents ?? 0,
        otherCostsPerSqmCents: inputs.otherCostsPerSqmCents ?? 0,
        rentSubsidyPct: inputs.rentSubsidyPct ?? 0,
        rentSubsidyMonths: inputs.rentSubsidyMonths ?? 0,
        units,
    });
}
/**
 * Legacy mapping (D-352): Kaufpreis gesamt → Kaufpreis; Erwerbsnebenkosten % cannot be split into Grunderwerbsteuer
 * and Notar → Notar / Grundbuch % with Grunderwerbsteuer 0; Sanierungskosten → Cap-Ex / Sanierung; Ziel-Verkaufspreis
 * je m² unchanged; Wohnfläche gesamt + Ist-Miete p. a. → one summary unit row (rent per month = p. a. ÷ 12, whole
 * cents). Every other input of Nico's model stays empty, so the results keep the old Einstand gesamt (= GIK) and the
 * old Marge (= VK-Basis − GIK without a sales commission).
 */
export function legacyAcquisitionInputs(legacy) {
    const inputs = {
        purchasePriceCents: legacy.purchasePriceTotalCents,
        brokerCommissionPct: null,
        landTransferTaxPct: legacy.acquisitionCostsPct === null ? null : 0,
        notaryPct: legacy.acquisitionCostsPct,
        capexCents: legacy.renovationCostsCents,
        financingCostsCents: null,
        divisionCostsCents: null,
        targetSalePricePerSqmCents: legacy.targetSalePricePerSqmCents,
        salesCommissionPct: null,
        interestPct: null,
        repaymentPct: null,
        depreciationPct: null,
        buildingSharePct: null,
        taxRatePct: null,
        buyerAcquisitionCostsPct: null,
        unitManagementCents: null,
        hoaManagementCents: null,
        maintenancePerSqmCents: null,
        otherCostsPerSqmCents: null,
        rentSubsidyPct: null,
        rentSubsidyMonths: null,
    };
    const units = legacy.livingAreaTotalSqm === null && legacy.actualRentPaCents === null
        ? []
        : [
            {
                position: '',
                number: ACQUISITION_SUMMARY_UNIT_NUMBER,
                areaSqm: legacy.livingAreaTotalSqm ?? 0,
                rentCents: legacy.actualRentPaCents === null ? 0 : Math.round(legacy.actualRentPaCents / 12),
                residential: true,
            },
        ];
    return { inputs, units };
}
/** The inputs a new calculation starts with: Nico's defaults and the property's land transfer tax (D-199). */
export function acquisitionStartInputs(landTransferTaxPct) {
    return {
        purchasePriceCents: null,
        brokerCommissionPct: ACQUISITION_DEFAULTS.brokerCommissionPct,
        landTransferTaxPct,
        notaryPct: ACQUISITION_DEFAULTS.notaryPct,
        capexCents: ACQUISITION_DEFAULTS.capexCents,
        financingCostsCents: ACQUISITION_DEFAULTS.financingCostsCents,
        divisionCostsCents: ACQUISITION_DEFAULTS.divisionCostsCents,
        targetSalePricePerSqmCents: null,
        salesCommissionPct: ACQUISITION_DEFAULTS.salesCommissionPct,
        interestPct: ACQUISITION_DEFAULTS.interestPct,
        repaymentPct: ACQUISITION_DEFAULTS.repaymentPct,
        depreciationPct: ACQUISITION_DEFAULTS.depreciationPct,
        buildingSharePct: ACQUISITION_DEFAULTS.buildingSharePct,
        taxRatePct: ACQUISITION_DEFAULTS.taxRatePct,
        buyerAcquisitionCostsPct: ACQUISITION_DEFAULTS.buyerAcquisitionCostsPct,
        unitManagementCents: ACQUISITION_DEFAULTS.unitManagementCents,
        hoaManagementCents: ACQUISITION_DEFAULTS.hoaManagementCents,
        maintenancePerSqmCents: ACQUISITION_DEFAULTS.maintenancePerSqmCents,
        otherCostsPerSqmCents: ACQUISITION_DEFAULTS.otherCostsPerSqmCents,
        rentSubsidyPct: ACQUISITION_DEFAULTS.rentSubsidyPct,
        rentSubsidyMonths: ACQUISITION_DEFAULTS.rentSubsidyMonths,
    };
}
/** Two unit lists are the same when every row matches field by field (order included). */
export function sameAcquisitionUnits(a, b) {
    return (a.length === b.length &&
        a.every((row, index) => {
            const other = b[index];
            return (other !== undefined &&
                row.position === other.position &&
                row.number === other.number &&
                row.areaSqm === other.areaSqm &&
                row.rentCents === other.rentCents &&
                row.residential === other.residential);
        }));
}
//# sourceMappingURL=acquisition-calculation.js.map